<script setup>
/**
 * Aceleres -- tela administrativa "Números da Equipe".
 *
 * Lista os contatos marcados (ativos por padrão, com filtro para incluir os
 * inativos, que permanecem disponíveis para auditoria), com pesquisa,
 * ordenação, paginação, acesso ao contato e ao histórico de movimentações.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useRoute, useRouter } from 'vue-router';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import TeamNumbersAPI from 'dashboard/api/aceleres/teamNumbers';
import TeamNumberHistoryModal from './TeamNumberHistoryModal.vue';

const route = useRoute();
const router = useRouter();

const records = ref([]);
const totalCount = ref(0);
const isLoading = ref(false);
const search = ref('');
const status = ref('active');
const sortBy = ref('marked_at');
const sortOrder = ref('desc');
const currentPage = ref(Number(route.query.page ?? 1));
const perPage = 25;
const historyContact = ref(null);

const accountId = computed(() => route.params.accountId);

const statusOptions = [
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Inativos' },
  { value: 'all', label: 'Todos' },
];

const sortOptions = [
  { value: 'marked_at', label: 'Marcado em' },
  { value: 'updated_at', label: 'Última alteração' },
  { value: 'name', label: 'Nome' },
];

const tableHeaders = [
  'Contato',
  'Número',
  'Marcado em',
  'Marcado por',
  'Última alteração',
  'Status',
  'Ações',
];

const fetchRecords = async () => {
  isLoading.value = true;
  try {
    const { data } = await TeamNumbersAPI.list({
      search: search.value || undefined,
      status: status.value,
      sort_by: sortBy.value,
      sort_order: sortOrder.value,
      page: currentPage.value,
      per_page: perPage,
    });
    records.value = data.payload || [];
    totalCount.value = data.meta?.total_count || 0;

    // Se a pagina atual ficou vazia (ex.: desmarcou o ultimo registro dela),
    // volta uma pagina em vez de deixar a tela sem resultados.
    if (!records.value.length && currentPage.value > 1 && totalCount.value) {
      currentPage.value -= 1;
      await fetchRecords();
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        'Não foi possível carregar os números da equipe.'
    );
  } finally {
    isLoading.value = false;
  }
};

const applyFilters = () => {
  currentPage.value = 1;
  // Mantem a URL coerente com o estado: sem isso um ?page=3 antigo voltaria a
  // valer no proximo reload.
  if (route.query.page) router.replace({ query: { ...route.query, page: 1 } });
  fetchRecords();
};

const onPageChange = page => {
  currentPage.value = page;
  router.push({ query: { ...route.query, page } });
  fetchRecords();
};

const toggleSortOrder = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  fetchRecords();
};

const contactLink = record =>
  frontendURL(`accounts/${accountId.value}/contacts/${record.contact.id}`);

const unmark = async record => {
  try {
    await TeamNumbersAPI.unmark(record.contact.id);
    useAlert('Marcação removida.');
    fetchRecords();
  } catch (error) {
    useAlert(
      error?.response?.data?.message || 'Não foi possível remover a marcação.'
    );
  }
};

const mark = async record => {
  try {
    await TeamNumbersAPI.mark(record.contact.id);
    useAlert('Contato marcado como Número da Equipe.');
    fetchRecords();
  } catch (error) {
    useAlert(
      error?.response?.data?.message || 'Não foi possível aplicar a marcação.'
    );
  }
};

const formatDate = value =>
  value
    ? messageTimestamp(new Date(value).getTime() / 1000, 'dd/MM/yyyy HH:mm')
    : '—';

watch(status, applyFilters);

onMounted(fetchRecords);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    loading-message="Carregando números da equipe..."
    :no-records-found="!records.length && !isLoading"
    no-records-message="Nenhum contato marcado como Número da Equipe."
  >
    <template #header>
      <BaseSettingsHeader
        title="Números da Equipe"
        description="Contatos internos marcados como Número da Equipe. Eles seguem trocando mensagens normalmente, mas ficam fora dos processamentos automáticos."
      />
    </template>
    <!--
      Os filtros ficam no preBody porque o SettingsLayout troca o slot `body`
      pelo estado vazio/carregando. Se ficassem no body, filtrar para um
      resultado sem registros removeria a busca e o seletor da tela, deixando o
      usuario sem como voltar.
    -->
    <template #preBody>
      <div class="flex flex-wrap items-center gap-2 pb-4">
        <input
          v-model="search"
          type="text"
          placeholder="Pesquisar por nome, telefone ou e-mail"
          class="w-72 !mb-0"
          @keyup.enter="applyFilters"
        />
        <select v-model="status" class="w-40 !mb-0">
          <option
            v-for="option in statusOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <select v-model="sortBy" class="w-44 !mb-0" @change="fetchRecords">
          <option
            v-for="option in sortOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <Button
          :label="sortOrder === 'asc' ? 'Crescente' : 'Decrescente'"
          size="small"
          variant="ghost"
          @click="toggleSortOrder"
        />
        <Button label="Pesquisar" size="small" @click="applyFilters" />
      </div>

      <TeamNumberHistoryModal
        v-if="historyContact"
        :contact="historyContact"
        @close="historyContact = null"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-4">
        <BaseTable :headers="tableHeaders" :items="records">
          <template #row="{ items }">
            <BaseTableRow
              v-for="record in items"
              :key="record.id"
              :item="record"
            >
              <template #default>
                <BaseTableCell>
                  <router-link
                    :to="contactLink(record)"
                    class="text-body-main text-n-slate-12 hover:underline"
                  >
                    {{ record.contact.name }}
                  </router-link>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11">
                    {{ record.contact.phone_number || '—' }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span
                    class="text-body-main text-n-slate-11 whitespace-nowrap"
                  >
                    {{ formatDate(record.marked_at) }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11">
                    {{ record.marked_by?.name || 'Sistema' }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span
                    class="text-body-main text-n-slate-11 whitespace-nowrap"
                  >
                    {{ formatDate(record.updated_at) }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span
                    class="px-2 py-0.5 text-xs rounded-md"
                    :class="
                      record.status === 'active'
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-slate-3 text-n-slate-11'
                    "
                  >
                    {{ record.status === 'active' ? 'Ativo' : 'Inativo' }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <div class="flex items-center gap-1">
                    <router-link :to="contactLink(record)">
                      <Button
                        label="Ver contato"
                        size="small"
                        variant="ghost"
                      />
                    </router-link>
                    <Button
                      label="Histórico"
                      size="small"
                      variant="ghost"
                      @click="historyContact = record.contact"
                    />
                    <Button
                      v-if="record.status === 'active'"
                      label="Desmarcar"
                      size="small"
                      variant="ghost"
                      color="ruby"
                      @click="unmark(record)"
                    />
                    <Button
                      v-else
                      label="Marcar"
                      size="small"
                      variant="ghost"
                      @click="mark(record)"
                    />
                  </div>
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>

        <PaginationFooter
          :current-page="currentPage"
          :total-items="totalCount"
          :items-per-page="perPage"
          @update:current-page="onPageChange"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
